import {
  useGetAllJobPostsQuery,
  useSyncJobStatusMutation,
} from '@/app/features/recruitment/job-applicants/job-applicants.api';
import { DISCLOSER_KEYS, FILTER_KEYS } from '@/constants/keys.constants';
import {
  Calendar03Icon,
  ChevronDownIcon,
  DownloadIcon,
  SearchIcon,
  UploadIcon,
} from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { Button, DatePicker, Flex, Input, Select } from 'antd';
import { useAppDiscloser, useAppFilters } from '../../../../../hooks';
import type { JobApplicantsSegmentType } from '../JobApplicants';

const { RangePicker } = DatePicker;

const JobPostFilters = () => {
  const { handleFilter, handleSearch } = useAppFilters(FILTER_KEYS.JOB_POST);
  const [, handleOpen] = useAppDiscloser(DISCLOSER_KEYS.MANAGE_JOB_POST);
  const [syncJobStatus, { isLoading: isSyncing }] = useSyncJobStatusMutation();

  const handleSync = async () => {
    try {
      await syncJobStatus({}).unwrap();
      toast('success', 'Job statuses synced successfully');
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to sync job statuses'));
    }
  };

  return (
    <div className="flex flex-col items-end gap-4">
      <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
        <Input
          allowClear
          size="large"
          onChange={handleSearch}
          placeholder="Search Batch"
          prefix={<SearchIcon size={20} />}
        />
        <RangePicker
          allowClear
          size="large"
          onChange={(values) => {
            const [startDate, endDate] = values || [];
            if (startDate && endDate) {
              handleFilter(
                'publish_date_start',
                startDate.format('YYYY-MM-DD'),
              );
              handleFilter('publish_date_end', endDate.format('YYYY-MM-DD'));
            } else {
              handleFilter('publish_date_start', undefined);
              handleFilter('publish_date_end', undefined);
            }
          }}
          suffixIcon={null}
          prefix={<Calendar03Icon size={20} />}
        />
        <Button disabled size="large" className="btn-primary-bordered">
          <DownloadIcon size={16} />
          Export
        </Button>

        <Button disabled size="large" className="btn-primary-bordered">
          Upload CSV
          <UploadIcon size={16} />
        </Button>
      </Flex>
      <Flex gap={12}>
        <Button
          size="large"
          className="btn-primary-bordered"
          loading={isSyncing}
          onClick={handleSync}
        >
          Sync Job Status
        </Button>
        <Button
          size="large"
          className="btn-primary"
          onClick={() => handleOpen({ type: 'ADD' })}
        >
          Create New Job post
        </Button>
      </Flex>
    </div>
  );
};

import useSelectionOptions from '@/hooks/useSelectionOptions';

const ApplicantsFilters = () => {
  const { handleFilter, handleSearch, filters } = useAppFilters(
    FILTER_KEYS.JOB_APPLICANT,
  );

  const [jobOptions, isJobsFetching] = useSelectionOptions(
    useGetAllJobPostsQuery,
    {
      labelKey: 'job_title',
      valueKey: 'id',
    },
  );

  return (
    <div className="flex flex-col items-end gap-4">
      <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
        <Input
          allowClear
          size="large"
          className="!w-[250px] shrink-0"
          onChange={handleSearch}
          placeholder="Search name, email"
          prefix={<SearchIcon size={20} />}
        />
        <Select
          allowClear
          size="large"
          placeholder="Select Stage"
          popupMatchSelectWidth={false}
          onChange={(val) => handleFilter('stage', val)}
          suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
          options={[
            { label: 'Screening', value: 'screening' },
            { label: 'Assessment', value: 'assessment' },
            { label: 'Interview', value: 'interview' },
            { label: 'Offer', value: 'offer' },
          ]}
        />
        <Select
          allowClear
          showSearch
          size="large"
          placement="bottomRight"
          value={filters.job_id}
          placeholder="Select Job"
          popupMatchSelectWidth={false}
          loading={isJobsFetching as boolean}
          onChange={(val) => handleFilter('job_id', val)}
          options={jobOptions as any[]}
          optionFilterProp="label"
          suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
        />
        <Button disabled size="large" className="btn-primary-bordered">
          <DownloadIcon size={16} />
          Export
        </Button>
      </Flex>
      <div className="h-[46px]" />
    </div>
  );
};
const ShortListedFilters = () => {
  const { handleFilter, handleSearch, filters } = useAppFilters(
    FILTER_KEYS.JOB_SHORTLISTED,
  );

  const [jobOptions, isJobsFetching] = useSelectionOptions(
    useGetAllJobPostsQuery,
    {
      labelKey: 'job_title',
      valueKey: 'id',
    },
  );

  return (
    <div className="flex flex-col items-end gap-4">
      <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
        <Input
          allowClear
          size="large"
          className="!w-[250px] shrink-0"
          onChange={handleSearch}
          placeholder="Search name, email"
          prefix={<SearchIcon size={20} />}
        />
        <div className="min-w-40">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placement="bottomRight"
            size="large"
            placeholder="Select Job"
            popupMatchSelectWidth={false}
            value={filters.job_id}
            loading={isJobsFetching as boolean}
            onChange={(val) => handleFilter('job_id', val)}
            options={jobOptions as any[]}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
          />
        </div>
        <Button disabled size="large" className="btn-primary-bordered">
          <DownloadIcon size={16} />
          Export
        </Button>
      </Flex>
      <div className="h-[46px]" />
    </div>
  );
};

const TalentHuntFilters = () => {
  const { handleSearch, handleFilter } = useAppFilters(FILTER_KEYS.TALENT_HUNT);

  return (
    <div className="flex flex-col items-end gap-4">
      <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
        <Input
          allowClear
          size="large"
          onChange={handleSearch}
          className="shrink-0 w-[225px]"
          placeholder="Search Jobs"
          prefix={<SearchIcon size={24} />}
        />
        <RangePicker
          allowClear
          size="large"
          onChange={(values) => {
            const [startDate, endDate] = values || [];
            if (startDate && endDate) {
              handleFilter(
                'publish_date_start',
                startDate.format('YYYY-MM-DD'),
              );
              handleFilter('publish_date_end', endDate.format('YYYY-MM-DD'));
            } else {
              handleFilter('publish_date_start', undefined);
              handleFilter('publish_date_end', undefined);
            }
          }}
          suffixIcon={null}
          prefix={<Calendar03Icon size={20} />}
        />
        <Button
          disabled
          size="large"
          className="btn-primary-bordered"
          icon={<DownloadIcon size={16} strokeWidth={2.5} />}
        >
          Export
        </Button>
      </Flex>
      <div className="h-[46px]" />
    </div>
  );
};

type Props = { type: JobApplicantsSegmentType };
const Filters = ({ type }: Props) => {
  switch (type) {
    case 'job-post':
      return <JobPostFilters />;
    case 'applicants':
      return <ApplicantsFilters />;
    case 'shortlisted':
      return <ShortListedFilters />;
    case 'talent-hunt':
      return <TalentHuntFilters />;
    default:
      return null;
  }
};
export default Filters;
